import React, { useEffect, useMemo, useRef } from 'react';

import { PlusIcon } from 'lucide-react';
import { FieldArrayWithId, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { FILTER_ACTIVE_CLASSES } from '@/components/ui/custom/ComplexFilter/filterStyles';
import { SearchableDropdownMenu } from '@/components/ui/custom/SearchableDropdownMenu/SearchableDropdownMenu';
import useMembers from '@/hooks/useMembers';
import { cn } from '@/lib/utils';
import {
  closeLogsFilterPanel,
  openLogsFilterPanel,
  setLogsFiltersDropdownOpen,
} from '@/modules/analytics/redux/actions';
import { selectLogsFiltersPanel } from '@/modules/analytics/redux/selectors';
import { FilterViewListItem } from '@/shared/types/analytics';

import { SessionFilterDropdown } from './SessionFilterDropdown';
import { mapUiToDataModelType } from './sessionFilterUtils';
import { useAccountFiltersData } from './useAccountFiltersData';
import { AccountFilterForm, AccountFilterFormValue } from './useAccountSessionFilters';

interface AccountFiltersDropdownProps {
  formMethods: UseFormReturn<AccountFilterForm>;
  fields: FieldArrayWithId<AccountFilterForm, 'filters', 'id'>[];
  addFilter: (filterType: string) => void;
  removeFilter: (index: number) => void;
  saveFilters: () => void;
  activeFilters: AccountFilterFormValue[];
  savedViews?: FilterViewListItem[];
  onLoadView?: (viewId: string) => void;
  allowedFilters?: string[];
}

/**
 * Account-scoped filters dropdown that mirrors the brain logs UX.
 */
export const AccountFiltersDropdown: React.FC<AccountFiltersDropdownProps> = ({
  formMethods,
  fields,
  addFilter,
  removeFilter,
  saveFilters,
  activeFilters,
  savedViews,
  onLoadView,
  allowedFilters,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openFilter, openFilterIndex, dropdownOpen, openSource } =
    useSelector(selectLogsFiltersPanel);
  const shouldOpenDropdownAfterClose = useRef(false);
  const { getMember } = useMembers();

  const closeFilterDropdown = () => {
    dispatch(closeLogsFilterPanel());
  };

  useEffect(() => {
    if (openFilter === null && shouldOpenDropdownAfterClose.current) {
      shouldOpenDropdownAfterClose.current = false;
      dispatch(setLogsFiltersDropdownOpen(true));
    }
  }, [dispatch, openFilter]);

  const handleBackToMenu = (source?: 'back' | 'outside') => {
    closeFilterDropdown();
    if (source === 'back') {
      dispatch(setLogsFiltersDropdownOpen(true));
    }
  };

  const handleFilterClick = (filterId: string) => {
    if (filterId.startsWith('view:')) {
      const viewId = filterId.replace('view:', '');
      if (onLoadView) {
        onLoadView(viewId);
      }
      dispatch(setLogsFiltersDropdownOpen(false));
      return;
    }

    // Quick-add for Environments (desks) via search-only items
    if (filterId.startsWith('desk:')) {
      const deskId = filterId.split(':')[1];
      const existingIndex = fields.findIndex((field) => field.type === 'deskIds');

      if (existingIndex >= 0) {
        const currentDeskIds: string[] =
          formMethods.getValues(`filters.${existingIndex}.deskIds`) || [];
        if (!currentDeskIds.includes(deskId)) {
          formMethods.setValue(`filters.${existingIndex}.deskIds`, [...currentDeskIds, deskId]);
        }
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'deskIds', filterIndex: existingIndex, source: 'menu' })
        );
        return;
      } else {
        const newIndex = fields.length;
        addFilter('deskIds');
        formMethods.setValue(`filters.${newIndex}.deskIds`, [deskId]);
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'deskIds', filterIndex: newIndex, source: 'menu' })
        );
      }
      return;
    }

    // Quick-add for Brains via search-only items
    if (filterId.startsWith('brain:')) {
      const brainId = filterId.split(':')[1];
      const existingIndex = fields.findIndex((field) => field.type === 'brainIds');

      if (existingIndex >= 0) {
        const currentBrainIds: string[] =
          formMethods.getValues(`filters.${existingIndex}.brainIds`) || [];
        if (!currentBrainIds.includes(brainId)) {
          formMethods.setValue(`filters.${existingIndex}.brainIds`, [...currentBrainIds, brainId]);
        }
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'brainIds', filterIndex: existingIndex, source: 'menu' })
        );
        return;
      } else {
        const newIndex = fields.length;
        addFilter('brainIds');
        formMethods.setValue(`filters.${newIndex}.brainIds`, [brainId]);
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'brainIds', filterIndex: newIndex, source: 'menu' })
        );
      }
      return;
    }

    // Quick-add for Agents via search-only items
    if (filterId.startsWith('agent:')) {
      const agentId = filterId.split(':')[1];
      const existingIndex = fields.findIndex((field) => field.type === 'agentIds');

      if (existingIndex >= 0) {
        const currentAgentIds: Array<{ value: string; label: string }> =
          formMethods.getValues(`filters.${existingIndex}.agentIds`) || [];
        const agentExists = currentAgentIds.some((agent) => agent.value === agentId);

        if (!agentExists) {
          formMethods.setValue(`filters.${existingIndex}.agentIds`, [
            ...currentAgentIds,
            { value: agentId, label: getMember(agentId)?.name ?? agentId },
          ]);
        }
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'agentIds', filterIndex: existingIndex, source: 'menu' })
        );
        return;
      } else {
        const newIndex = fields.length;
        addFilter('agentIds');
        formMethods.setValue(`filters.${newIndex}.agentIds`, [
          { value: agentId, label: getMember(agentId)?.name ?? agentId },
        ]);
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'agentIds', filterIndex: newIndex, source: 'menu' })
        );
      }
      return;
    }

    if (filterId.startsWith('channel:')) {
      const channel = filterId.split(':')[1];
      const existingIndex = fields.findIndex((field) => field.type === 'channels');

      if (existingIndex >= 0) {
        const currentChannels: string[] =
          formMethods.getValues(`filters.${existingIndex}.channels`) || [];
        if (!currentChannels.includes(channel)) {
          formMethods.setValue(`filters.${existingIndex}.channels`, [...currentChannels, channel]);
        }
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'channels', filterIndex: existingIndex, source: 'menu' })
        );
        return;
      } else {
        const newIndex = fields.length;
        addFilter('channels');
        formMethods.setValue(`filters.${newIndex}.channels`, [channel]);
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'channels', filterIndex: newIndex, source: 'menu' })
        );
      }
      return;
    }

    if (filterId.startsWith('insight-')) {
      const insightFiltersIndex = fields.findIndex((field) => field.type === 'insightFilters');

      if (insightFiltersIndex >= 0) {
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({
            filterKey: filterId,
            filterIndex: insightFiltersIndex,
            source: 'menu',
          })
        );
        return;
      } else {
        const newIndex = fields.length;
        addFilter('insightFilters');
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: filterId, filterIndex: newIndex, source: 'menu' })
        );
      }
      return;
    }

    let dataModelType = filterId;
    if (filterId === 'language-models') {
      dataModelType = 'languageModelIds';
    }

    const existingIndex = fields.findIndex((field) => field.type === dataModelType);

    if (existingIndex >= 0) {
      dispatch(setLogsFiltersDropdownOpen(false));
      dispatch(
        openLogsFilterPanel({ filterKey: filterId, filterIndex: existingIndex, source: 'menu' })
      );
    } else {
      const newIndex = fields.length;
      addFilter(dataModelType);
      dispatch(setLogsFiltersDropdownOpen(false));
      dispatch(openLogsFilterPanel({ filterKey: filterId, filterIndex: newIndex, source: 'menu' }));
    }
  };

  const { menuGroups } = useAccountFiltersData(
    handleFilterClick,
    savedViews,
    onLoadView,
    allowedFilters
  );

  const activeFilterCount = activeFilters?.length || 0;

  // Keep the open account-level editor aligned with the latest field index after persistence.
  const resolvedFilterIndex = useMemo(() => {
    if (!openFilter || openFilterIndex === null) {
      return null;
    }

    const dataModelType = openFilter.startsWith('insight-')
      ? 'insightFilters'
      : mapUiToDataModelType(openFilter);

    const latestIndex = fields.findIndex((field) => field.type === dataModelType);
    if (latestIndex !== -1) {
      return latestIndex;
    }

    return openFilterIndex;
  }, [fields, openFilter, openFilterIndex]);

  const effectiveFilterIndex =
    resolvedFilterIndex !== null && resolvedFilterIndex !== undefined
      ? resolvedFilterIndex
      : openFilterIndex;

  const handleDropdownOpenChange = (newOpen: boolean) => {
    if (newOpen && openFilter && openFilterIndex !== null) {
      shouldOpenDropdownAfterClose.current = true;
      closeFilterDropdown();
      return;
    }
    dispatch(setLogsFiltersDropdownOpen(newOpen));
  };

  return (
    <div className="relative">
      <SearchableDropdownMenu
        trigger={
          <Button
            variant="outline-neutral"
            size="sm"
            className={cn(activeFilterCount > 0 && FILTER_ACTIVE_CLASSES)}
          >
            <PlusIcon className="h-4 w-4" />
            {t('common.filters')} {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
        }
        groups={menuGroups}
        searchPlaceholder={t('brain_logs.searchFilters')}
        emptyMessage={t('brain_logs.no_filters_found')}
        maxHeight="max-h-[576px]"
        align="end"
        isOpen={dropdownOpen}
        onOpenChange={handleDropdownOpenChange}
      />
      {openFilter && effectiveFilterIndex !== null && openSource !== 'pill' && (
        <SessionFilterDropdown
          type={openFilter}
          key={openFilter}
          filterIndex={effectiveFilterIndex}
          onClose={handleBackToMenu}
          onApply={saveFilters}
          scope="account"
          onClear={() => {
            if (effectiveFilterIndex !== null) {
              removeFilter(effectiveFilterIndex);
            }
            saveFilters();
            closeFilterDropdown();
          }}
        />
      )}
    </div>
  );
};
